import type { Classification } from '@repo/shared';

/** Turn an ISO-3166 alpha-2 code into its flag emoji (regional indicators). */
export function flagEmoji(country: string | null): string {
  if (!country || country.length !== 2) return '🏴';
  const code = country.toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '🏴';
  const base = 0x1f1e6;
  return String.fromCodePoint(base + (code.charCodeAt(0) - 65), base + (code.charCodeAt(1) - 65));
}

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' });
  } catch {
    return null;
  }
})();

/** Full country name in the viewer's locale ("NL" → "Netherlands"); falls back to the code. */
export function countryName(code: string | null): string {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return code ?? 'unknown';
  try {
    return regionNames?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/**
 * Time-of-day in the viewer's own locale and timezone (e.g. `14:03:07` or
 * `2:03:07 PM`, depending on the browser's locale). Falls back to a fixed
 * 24-hour render only if `Intl` is unavailable.
 */
export function clockTime(ts: number): string {
  const d = new Date(ts);
  try {
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
}

/**
 * Full date + time in the viewer's locale and timezone, with the timezone name
 * appended — used for hover tooltips so the terse clock always has an
 * unambiguous, local reference (e.g. `Jul 6, 2026, 2:03:07 PM PDT`).
 */
export function localeTimestamp(ts: number): string {
  const d = new Date(ts);
  try {
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'long',
    });
  } catch {
    return d.toISOString();
  }
}

/** Compact "time ago" for the uptime line. */
export function since(ts: number | null): string {
  if (ts == null) return '—';
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

/** Human label per classification, for legends/badges. */
export const CLASSIFICATION_LABEL: Record<Classification, string> = {
  wordpress: 'WordPress',
  'credential-stuffing': 'Cred stuffing',
  'env-leak': 'Env / secrets',
  'git-exposure': 'VCS exposure',
  'admin-probe': 'Admin panel',
  'cve-probe': 'CVE probe',
  'path-traversal': 'Path traversal',
  'shell-upload': 'Shell / RCE',
  'iot-botnet': 'IoT / router',
  'proxy-abuse': 'Proxy abuse',
  scanner: 'Scanner',
  recon: 'Recon',
};

/** A CSS custom-property colour token per classification. */
export const CLASSIFICATION_COLOR: Record<Classification, string> = {
  wordpress: 'var(--cyan)',
  'credential-stuffing': 'var(--red)',
  'env-leak': 'var(--amber)',
  'git-exposure': 'var(--amber)',
  'admin-probe': 'var(--magenta)',
  'cve-probe': 'var(--red)',
  'path-traversal': 'var(--violet)',
  'shell-upload': 'var(--red)',
  'iot-botnet': 'var(--violet)',
  'proxy-abuse': 'var(--magenta)',
  scanner: 'var(--green-dim)',
  recon: 'var(--text-dim)',
};

/** Truncate a long string with an ellipsis for tight UI slots. */
export function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}
